import { useMemo } from "react";
import { get } from "lodash";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import TagCategoryService from "services/TagCategory";
import { addResizedColumns } from "./useTableConfig";
import { TAG_CATEGORY_TABLE_CONSTANTS } from "routes/tag_category/TagCategoryTableV3";
import UserService from "services/User";
import {
  getTagCategoryTableColumns,
  getTagCategoryTableFilters,
} from "routes/tag_category/TagCategoryTableColumnsAndFilters";

export const invalidateTagCategoryTableQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [TAG_CATEGORY_TABLE_CONSTANTS.queryKey],
  });
};

export const useTagCategoryMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => TagCategoryService.createTagCategory(data),
    onSuccess: () => invalidateTagCategoryTableQuery(),
    onError: err =>
      toast.error(formatError(err, "Failed to save tag category")),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => TagCategoryService.editTagCategory(id, data),
    onSuccess: () => invalidateTagCategoryTableQuery(),
    onError: err =>
      toast.error(formatError(err, "Failed to save tag category")),
  });

  const remove = useMutation({
    mutationFn: id => TagCategoryService.deleteTagCategory(id),
    onSuccess: () => {
      invalidateTagCategoryTableQuery();
      toast.success("Tag category deleted");
      onDeleted?.();
    },
    onError: err =>
      toast.error(formatError(err, "Failed to delete tag category")),
  });

  return { create, update, remove };
};

export const useTagCategoryTable = ({ tableName }) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    return getTagCategoryTableFilters();
  }, []);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getTagCategoryTableColumns({
      onSuccess: invalidateTagCategoryTableQuery,
    });

    const tagCategoryTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: tagCategoryTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [tableName, tableSettings, isTableSettingsFetched]);

  return { filters, columns };
};
